import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ConfigBundle,
  type DiffConfigurationResult,
} from '@whinerp/client';
import { Button, InlineAction, Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Setup > Environments (F-025).
 *
 * The brief: "Never apply a config import wholesale. The diff review screen is
 * the feature; the file format is just plumbing." So the screen is the diff -
 * item by item, with a checkbox on each, and an apply button that names how
 * many it will apply.
 *
 * Items already identical are listed and NOT selectable: ticking something
 * that would change nothing is a click that teaches somebody the ticks do not
 * mean anything.
 */
export function EnvironmentsPanel(): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const [pasted, setPasted] = useState('');
  const [diff, setDiff] = useState<DiffConfigurationResult | null>(null);
  const [bundle, setBundle] = useState<ConfigBundle | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [outcome, setOutcome] = useState<string | null>(null);
  const { session, refresh } = useSession();
  const sandbox = useMutation({
    mutationFn: (isSandbox: boolean) => api.setSandbox({ body: { isSandbox } }),
    onSuccess: () => refresh(),
  });

  const exportOne = useMutation<ConfigBundle, ApiError>({
    mutationFn: () => api.exportConfiguration({}),
    onSuccess: (result) => setPasted(JSON.stringify(result, null, 2)),
  });

  const review = useMutation<DiffConfigurationResult, ApiError, ConfigBundle>({
    mutationFn: (parsed) => api.diffConfiguration({ body: { bundle: parsed } }),
    onSuccess: (result) => {
      setDiff(result);
      setSelected(new Set());
      setOutcome(null);
    },
  });

  const apply = useMutation({
    mutationFn: () =>
      api.applyConfiguration({
        body: { bundle: bundle!, selected: [...selected] },
      }),
    onSuccess: (result) => {
      setOutcome(
        t('env.applied', {
          values: { count: String(result.applied.length), skipped: String(result.skipped.length) },
        }),
      );
      setSelected(new Set());
    },
  });

  const entries = diff?.entries ?? [];
  const actionable = entries.filter((e) => e.verdict !== 'identical');

  return (
    <Panel data-region="environments" id="environments">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('env.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('env.body')}</p>
        <label className="flex items-center gap-2 text-dense text-ink">
          <input
            type="checkbox"
            data-mark-sandbox
            checked={session.workspace.isSandbox}
            disabled={sandbox.isPending}
            onChange={(event) => sandbox.mutate(event.target.checked)}
          />
          {t('wsSandbox.mark')}
        </label>
        <p className="text-meta text-ink-meta">{t('wsSandbox.how')}</p>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            intent="secondary"
            size="small"
            data-env-export
            busy={exportOne.isPending}
            onClick={() => exportOne.mutate()}
          >
            {t('env.export')}
          </Button>
        </div>

        <label className="flex flex-col gap-1 text-dense text-ink-600">
          {t('env.paste')}
          <textarea
            data-env-bundle
            value={pasted}
            onChange={(event) => setPasted(event.currentTarget.value)}
            rows={4}
            className="rounded-control border border-control bg-surface p-2 font-mono text-meta text-ink"
          />
        </label>

        <div>
          <Button
            intent="secondary"
            size="small"
            data-env-review
            busy={review.isPending}
            disabled={pasted.trim() === ''}
            onClick={() => {
              try {
                const parsed = JSON.parse(pasted) as ConfigBundle;
                setBundle(parsed);
                review.mutate(parsed);
              } catch {
                setOutcome(t('state.error'));
              }
            }}
          >
            {t('env.review')}
          </Button>
        </div>

        {review.isError ? (
          <p data-env-refusal className="text-dense text-danger-text">
            {errorMessage(review.error)}
          </p>
        ) : null}

        {diff ? (
          <div data-env-diff className="flex flex-col gap-2">
            <InlineAction
              data-env-select-changed
              onClick={() => setSelected(new Set(actionable.map((e) => `${e.kind}:${e.key}`)))}
            >
              {t('env.selectAllChanged')}
            </InlineAction>

            <ul className="flex flex-col">
              {entries.map((entry) => {
                const id = `${entry.kind}:${entry.key}`;
                const identical = entry.verdict === 'identical';
                return (
                  <li
                    key={id}
                    data-env-item={id}
                    data-env-verdict={entry.verdict}
                    className="flex flex-col gap-0.5 border-b border-b-rule py-2"
                  >
                    <label className="flex items-baseline gap-2 text-dense text-ink">
                      <input
                        type="checkbox"
                        data-env-pick={id}
                        // Already the same: ticking it would be a click that
                        // changes nothing, which teaches people the ticks do
                        // not mean anything.
                        disabled={identical}
                        checked={selected.has(id)}
                        onChange={(event) => {
                          const on = event.currentTarget.checked;
                          setSelected((current) => {
                            const next = new Set(current);
                            if (on) next.add(id);
                            else next.delete(id);
                            return next;
                          });
                        }}
                      />
                      <span>{entry.label}</span>
                      <span className="rounded-chip bg-sunken px-2 py-0.5 text-meta text-ink-600">
                        {identical
                          ? t('env.identical')
                          : entry.verdict === 'new'
                            ? t('env.new')
                            : t('env.changed')}
                      </span>
                    </label>

                    {/* The diff itself, field by field. A checkbox beside a
                        name tells somebody nothing about what they are about
                        to change. */}
                    {entry.changedFields.length > 0 ? (
                      <ul className="flex flex-col ps-6">
                        {entry.changedFields.map((change) => (
                          <li
                            key={`${id}:${change.field}`}
                            data-env-change={change.field}
                            className="text-meta text-ink-600"
                          >
                            {change.field}: {change.from ?? '—'} → {change.to ?? '—'}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>

            {diff.onlyHere.length > 0 ? (
              <p data-env-only-here className="text-meta text-ink-meta">
                {t('env.onlyHere', { values: { count: String(diff.onlyHere.length) } })}
              </p>
            ) : null}

            <div className="flex items-center gap-2">
              <Button
                intent="primary"
                size="small"
                data-env-apply
                busy={apply.isPending}
                disabled={selected.size === 0}
                onClick={() => apply.mutate()}
              >
                {t('env.apply', { values: { count: String(selected.size) } })}
              </Button>
              {selected.size === 0 ? (
                <span className="text-meta text-ink-meta">{t('env.nothingSelected')}</span>
              ) : null}
            </div>
          </div>
        ) : null}

        {outcome ? (
          <p data-env-outcome className="text-dense text-ink">
            {outcome}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
